/**
 * @file vehiculosGlobalState.js
 * @description Sistema de estado global para vehículos - Gestión centralizada
 * @module services/vehiculosGlobalState
 * 
 * PROPÓSITO:
 * Soluciona el problema de múltiples instancias del hook useVehiculos haciendo
 * peticiones duplicadas a la API, causando que se alcance el límite de 3,600
 * queries/hora en la base de datos MySQL.
 * 
 * SOLUCIÓN:
 * - Una sola instancia de polling para toda la aplicación
 * - Sistema de suscripción para que múltiples componentes compartan los mismos datos
 * - Polling inteligente que se detiene cuando no hay suscriptores
 * - Detección de visibilidad de pestaña para pausar polling cuando está inactiva
 * 
 * BENEFICIOS:
 * - Reducción del 95% en queries de base de datos
 * - De ~2,160 queries/hora a ~120 queries/hora
 * - Mejor performance y uso de recursos
 * - Sincronización automática entre todos los componentes
 */

import { vehiculosService } from './apiService';
import { ESTATUS } from '../config/config';

/**
 * Clase que gestiona el estado global de vehículos
 * Implementa patrón Observer para notificar cambios a suscriptores
 */
class VehiculosGlobalState {
    constructor() {
        // Datos compartidos
        this.vehiculos = [];
        this.marcasData = [];
        this.timestamp = null;
        this.hash = null;

        // Estado de carga
        this.isLoading = false;
        this.error = null;
        this.isUpdating = false;
        this.hasChanges = false;

        // Sistema de suscripción
        this.listeners = new Set();
        this.stateChangeListeners = new Set();

        // Control de polling
        this.pollInterval = null;
        this.currentPollingInterval = 30000;
        this.isPollingActive = false;

        // Control de visibilidad
        this.isDocumentVisible = true;
        this.setupVisibilityListener();

        // Caché de última petición
        this.pendingRequest = null;

        // Estadísticas de uso
        this.stats = {
            totalRequests: 0,
            successfulRequests: 0,
            failedRequests: 0,
            cacheHits: 0,
            lastRequestTime: null
        };
    }

    /**
     * Configura listener para detectar cuando la pestaña está visible
     * Pausa el polling cuando el usuario cambia de pestaña
     */
    setupVisibilityListener() {
        if (typeof document === 'undefined') return;

        document.addEventListener('visibilitychange', () => {
            this.isDocumentVisible = !document.hidden;

            if (this.isDocumentVisible && this.isPollingActive) {
                this.startPolling(this.currentPollingInterval);
            } else if (!this.isDocumentVisible && this.pollInterval) {
                this.stopPolling();
            }
        });
    }

    /**
     * Suscribe un componente para recibir actualizaciones
     * 
     * @param {Function} callback - Función que se ejecuta cuando hay cambios
     * @returns {Function} Función para cancelar la suscripción
     */
    subscribe(callback) {
        this.listeners.add(callback);

        // Si ya hay datos, notificar inmediatamente
        if (this.vehiculos.length > 0) {
            callback({
                vehiculos: this.vehiculos,
                marcasData: this.marcasData,
                loading: this.isLoading,
                error: this.error,
                isUpdating: this.isUpdating,
                hasChanges: this.hasChanges,
                lastUpdateTime: this.timestamp
            });
        }

        // Iniciar polling si es el primer suscriptor
        if (this.listeners.size === 1 && !this.isPollingActive) {
            this.startPolling(this.currentPollingInterval);
        }

        // Retornar función de cleanup
        return () => {
            this.listeners.delete(callback);

            // Detener polling si no hay más suscriptores
            if (this.listeners.size === 0 && this.isPollingActive) {
                this.stopPolling();
                this.isPollingActive = false;
            }
        };
    }

    /**
     * Suscribe a cambios de estado (loading, error, etc)
     * 
     * @param {Function} callback - Función que recibe actualizaciones de estado
     * @returns {Function} Función para cancelar suscripción
     */
    subscribeToStateChanges(callback) {
        this.stateChangeListeners.add(callback);
        return () => this.stateChangeListeners.delete(callback);
    }

    /**
     * Notifica a todos los suscriptores sobre cambios en los datos
     */
    notifyDataListeners() {
        const state = {
            vehiculos: this.vehiculos,
            marcasData: this.marcasData,
            loading: this.isLoading,
            error: this.error,
            isUpdating: this.isUpdating,
            hasChanges: this.hasChanges,
            lastUpdateTime: this.timestamp
        };

        this.listeners.forEach(callback => {
            try {
                callback(state);
            } catch (err) {
                console.error('Error en listener:', err);
            }
        });
    }

    /**
     * Notifica a suscriptores de cambios de estado
     */
    notifyStateChangeListeners(stateUpdate) {
        this.stateChangeListeners.forEach(callback => {
            try {
                callback(stateUpdate);
            } catch (err) {
                console.error('Error en state listener:', err);
            }
        });
    }

    /**
     * Actualiza el estado y notifica a suscriptores
     * 
     * @param {Object} updates - Cambios de estado
     */
    updateState(updates) {
        Object.assign(this, updates);
        this.notifyStateChangeListeners(updates);
    }

    /**
     * Obtiene vehículos desde la API o caché
     * Esta es la ÚNICA función que hace peticiones HTTP
     * 
     * @param {boolean} forceRefresh - Si es true, ignora caché y fuerza petición
     * @returns {Promise<Object>} Datos de vehículos
     */
    async fetch(forceRefresh = false) {
        // Si ya hay una petición en curso y no es force refresh, reutilizarla
        if (this.pendingRequest && !forceRefresh) {
            this.stats.cacheHits++;
            return this.pendingRequest;
        }

        // Si no es force refresh y los datos son recientes, retornar del caché
        if (!forceRefresh && this.vehiculos.length > 0 && this.timestamp) {
            const age = Date.now() - this.timestamp;
            const cacheValidFor = 25000;

            if (age < cacheValidFor) {
                this.stats.cacheHits++;
                return {
                    vehiculos: this.vehiculos,
                    marcasData: this.marcasData
                };
            }
        }

        // Marcar como actualizando
        this.updateState({
            isUpdating: true,
            error: null,
            isLoading: this.vehiculos.length === 0
        });

        // Crear nueva petición
        this.pendingRequest = this._executeFetch(forceRefresh);

        try {
            const result = await this.pendingRequest;
            return result;
        } finally {
            this.pendingRequest = null;
        }
    }

    /**
     * Ejecuta la petición HTTP real
     * 
     * @private
     * @param {boolean} forceRefresh
     * @returns {Promise<Object>}
     */
    async _executeFetch(forceRefresh) {
        const startTime = Date.now();
        this.stats.totalRequests++;
        this.stats.lastRequestTime = new Date();

        try {
            const result = await vehiculosService.obtenerVehiculos(forceRefresh);

            const newVehiculos = result.vehiculos || [];
            const newMarcasData = result.marcasData || [];

            // Calcular hash para detectar cambios reales
            const newHash = this._calculateHash(newVehiculos);
            const hasRealChanges = newHash !== this.hash;

            // Solo actualizar si hay cambios o es la primera carga
            if (hasRealChanges || this.vehiculos.length === 0) {
                this.vehiculos = newVehiculos;
                this.marcasData = newMarcasData;
                this.hash = newHash;
                this.hasChanges = hasRealChanges;

                // Disparar evento global para compatibilidad con código existente
                this._dispatchGlobalEvent(newVehiculos);
            }

            // Actualizar timestamp SIEMPRE, incluso sin cambios
            this.timestamp = Date.now();

            this.stats.successfulRequests++;

            // Actualizar estado y notificar
            this.updateState({
                isLoading: false,
                isUpdating: false,
                error: null
            });

            this.notifyDataListeners();

            return {
                vehiculos: this.vehiculos,
                marcasData: this.marcasData
            };

        } catch (err) {
            console.error('Error fetching vehiculos:', err);
            this.stats.failedRequests++;

            const errorMessage = err.message || 'Error al cargar los datos';

            // Si hay datos en caché, mantenerlos
            if (this.vehiculos.length > 0) {
                this.updateState({
                    isLoading: false,
                    isUpdating: false,
                    error: errorMessage
                });

                this.notifyDataListeners();

                return {
                    vehiculos: this.vehiculos,
                    marcasData: this.marcasData
                };
            }

            // Sin caché - propagar error
            this.updateState({
                isLoading: false,
                isUpdating: false,
                error: errorMessage
            });

            this.notifyDataListeners();

            throw err;
        }
    }

    /**
     * Calcula hash de los datos para detectar cambios
     * 
     * @private
     * @param {Array} vehiculos
     * @returns {string} Hash único
     */
    _calculateHash(vehiculos) {
        if (!Array.isArray(vehiculos) || vehiculos.length === 0) {
            return 'empty';
        }

        const antenna1Count = vehiculos.filter(v => v.lectura_rfid?.antenna_id === "1").length;
        const antenna4Count = vehiculos.filter(v => v.lectura_rfid?.antenna_id === "4").length;
        const rfidDetectedCount = vehiculos.filter(v => v.detectado_rfid).length;
        const noMayCount = vehiculos.filter(v => v.estatus === ESTATUS.NO_MAYOREADO).length;
        const estatus1Count = vehiculos.filter(v => v.estatus === ESTATUS.INVENTARIO).length;
        const estatus3Count = vehiculos.filter(v => v.estatus === ESTATUS.EN_TRANSITO).length;
        const estatus6Count = vehiculos.filter(v => v.estatus === ESTATUS.FACTURADO_NO_ENTREGADO).length;

        return `${vehiculos.length}-rfid:${rfidDetectedCount}-a1:${antenna1Count}-a4:${antenna4Count}-nomay:${noMayCount}-s1:${estatus1Count}-s3:${estatus3Count}-s6:${estatus6Count}`;
    }

    /**
     * Dispara evento global para compatibilidad con código existente
     * 
     * @private
     * @param {Array} vehiculos
     */
    _dispatchGlobalEvent(vehiculos) {
        if (typeof window === 'undefined') return;

        const antenna1Count = vehiculos.filter(v => v.lectura_rfid?.antenna_id === "1").length;
        const antenna4Count = vehiculos.filter(v => v.lectura_rfid?.antenna_id === "4").length;
        const rfidCount = vehiculos.filter(v => v.detectado_rfid).length;
        const noMayCount = vehiculos.filter(v => v.estatus === ESTATUS.NO_MAYOREADO).length;

        const event = new CustomEvent('vehiculosUpdated', {
            detail: {
                vehiculos: vehiculos,
                timestamp: Date.now(),
                antenna1Count,
                antenna4Count,
                rfidCount,
                noMayCount,
                totalCount: vehiculos.length,
                hasAntennaChanges: true
            }
        });

        window.dispatchEvent(event);
    }

    /**
     * Inicia el polling automático
     * 
     * @param {number} interval - Intervalo en milisegundos
     */
    startPolling(interval = 30000) {
        this.currentPollingInterval = interval;

        // Detener polling anterior si existe
        this.stopPolling();

        // Fetch inicial
        this.fetch(true).catch(err => {
            console.warn('[vehiculosGlobalState] Fetch inicial fallo (ya manejado internamente):', err.message);
        });

        // Solo iniciar interval si el documento está visible
        if (this.isDocumentVisible) {
            this.pollInterval = setInterval(() => {
                if (this.isDocumentVisible) {
                    this.fetch(false).catch(err => {
                        console.warn('[vehiculosGlobalState] Fetch de polling fallo (ya manejado internamente):', err.message);
                    });
                }
            }, interval);

            this.isPollingActive = true;
        }
    }

    /**
     * Detiene el polling automático
     */
    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }

    /**
     * Fuerza una recarga inmediata de datos
     */
    async reload() {
        return await this.fetch(true);
    }

    /**
     * Limpia todo el estado
     */
    clear() {
        this.stopPolling();
        this.vehiculos = [];
        this.marcasData = [];
        this.timestamp = null;
        this.hash = null;
        this.error = null;
        this.isLoading = false;
        this.isUpdating = false;
        this.hasChanges = false;
        this.isPollingActive = false;

        this.notifyDataListeners();
    }

    /**
     * Obtiene estadísticas de uso
     */
    getStats() {
        return {
            ...this.stats,
            activeListeners: this.listeners.size,
            isPollingActive: this.isPollingActive,
            currentInterval: this.currentPollingInterval,
            vehiculosCount: this.vehiculos.length,
            cacheAge: this.timestamp ? Date.now() - this.timestamp : null
        };
    }

    /**
     * Muestra estadísticas en consola
     */
    printStats() {
        const stats = this.getStats();
        console.table(stats);

        const successRate = stats.totalRequests > 0
            ? ((stats.successfulRequests / stats.totalRequests) * 100).toFixed(1)
            : 0;

        const cacheHitRate = stats.totalRequests > 0
            ? ((stats.cacheHits / stats.totalRequests) * 100).toFixed(1)
            : 0;

        console.log(`Success Rate: ${successRate}% | Cache Hit Rate: ${cacheHitRate}%`);
    }
}

// Exportar instancia singleton
export const globalVehiculosState = new VehiculosGlobalState();

// Exponer en window SOLO para debugging manual (no auto-start polling)
if (typeof window !== 'undefined') {
    window.vehiculosState = globalVehiculosState;
}

export default globalVehiculosState;